import { Box, Typography } from '@mui/material';
import parse from 'html-react-parser';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import Checkbox from '@Components/Checkbox';
import { ImageBox } from '@Components/EmailPreview/EmailPreview.styles';
import { Button } from '@Components/index';
import { CommonConstants } from '@Constants/index';
import EmailFileAttachment from '@Containers/Emails/EmailFileAttachment';
import { GlobalEmailResponse } from '@Models/emails.models';
import { GlobalScriptResponse } from '@Models/scripts.models';
import { useAddEmailsFromGroupMutation } from '@Services/email.service';
import { useGetGroupTemplatesQuery } from '@Services/groups.service';
import { useAddScriptsFromGroupMutation } from '@Services/scripts.service';
import query from '@Utils/common/query.utils';

import { LibraryTemplatesProps } from './LibraryTemplates.types';

const LibraryTemplates: FunctionComponent<LibraryTemplatesProps> = (
  props: LibraryTemplatesProps
): JSX.Element => {
  const { groupId, type, closeModal } = props;

  const userId =
    query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)?.toString() || null;

  const [templates, setTemplates] = useState<GlobalScriptResponse[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [lastSelectedId, setLastSelectedId] = useState<string>('');
  const [templatePreview, setTemplatePreview] = useState<
    GlobalScriptResponse | GlobalEmailResponse | undefined
  >(undefined);

  const { data } = useGetGroupTemplatesQuery(
    { id: groupId || '', type },
    {
      skip: !groupId,
    }
  );
  const [addScripts, addScriptsMethods] = useAddScriptsFromGroupMutation();
  const [addEmails, addEmailsMethods] = useAddEmailsFromGroupMutation();

  const handleSelection = (id: string, checked: boolean) => {
    if (checked) {
      setSelected((prevState) => [...prevState, id]);
    } else {
      setSelected((prevState) => prevState.filter((i) => i !== id));
    }
  };

  const isSelected = useCallback((id) => selected.includes(id), [selected]);

  const handleSelectAll = () => {
    if (!isAllSelected) {
      const ids = templates.map((t) => t.id);
      setSelected(ids);
      setIsAllSelected(true);
    } else {
      setSelected([]);
      setIsAllSelected(false);
    }
  };

  const handleAddToDialer = () => {
    switch (type) {
      case 'global_scripts':
        return addScripts({ globalScriptsIds: selected, userId });
      case 'global_emails':
        return addEmails({ globalEmailsIds: selected, userId });
      default:
        console.log('wrong type');
    }
  };

  useEffect(() => {
    if (data && groupId) {
      setTemplates(data);
    }
    if (!groupId) {
      setTemplates([]);
    }
  }, [data, groupId]);

  useEffect(() => {
    if (templates?.length > 0 && selected.length > 0) {
      if (templates.length === selected.length) {
        setIsAllSelected(true);
      }
    }
  }, [templates, selected]);

  useEffect(() => {
    if (selected.length > 0) {
      setLastSelectedId(selected[selected.length - 1]);
    }
  }, [selected]);

  useEffect(() => {
    if (lastSelectedId !== '') {
      const template = templates.find((t) => t.id === lastSelectedId);
      if (type === 'global_scripts') {
        setTemplatePreview(template);
      } else if (type === 'global_emails') {
        setTemplatePreview(template);
      }
    }
  }, [lastSelectedId, templates, type]);

  useEffect(() => {
    if (addScriptsMethods.isSuccess || addEmailsMethods.isSuccess) {
      closeModal();
    }
  }, [addScriptsMethods.isSuccess, addEmailsMethods.isSuccess, closeModal]);

  const renderPreview = () => {
    if (templatePreview) {
      if (type === 'global_scripts') {
        return (
          <Box sx={{ overflow: 'auto', height: '300px' }}>
            <Typography component="p">
              {parse((templatePreview as GlobalScriptResponse).content)}
            </Typography>
          </Box>
        );
      }
      if (type === 'global_emails') {
        return (
          <Box sx={{ overflow: 'auto', height: '300px' }}>
            <Typography variant="h2">
              {(templatePreview as GlobalEmailResponse)?.subject}
            </Typography>
            {!!(templatePreview as GlobalEmailResponse)?.headerUrl && (
              <ImageBox>
                <img
                  src={(templatePreview as GlobalEmailResponse)?.headerUrl}
                  alt="email_header_img"
                />
              </ImageBox>
            )}
            <Typography component="p">
              {parse((templatePreview as GlobalEmailResponse)?.message)}
            </Typography>
            {!!(templatePreview as GlobalEmailResponse)?.footerUrl && (
              <ImageBox>
                <img
                  src={(templatePreview as GlobalEmailResponse)?.footerUrl}
                  alt="email_header_img"
                />
              </ImageBox>
            )}
            <Box mb={2.4}>
              {!!(templatePreview as GlobalEmailResponse)?.attachments
                .length && (
                <EmailFileAttachment
                  attachments={
                    (templatePreview as GlobalEmailResponse)?.attachments
                  }
                  viewOnly
                  uploadBtnText="hello"
                  uploadBtnIcon="attachment"
                  acceptedFormat="image/*"
                  isPreview
                />
              )}
            </Box>
          </Box>
        );
      }
    }
  };

  return (
    <Box mt={3.8}>
      <Box display="flex" justifyContent="space-between" gap="20px">
        <Box>
          {templates?.map(({ id, name }) => (
            <Box
              key={`${id}-${name}`}
              display="flex"
              onClick={() => handleSelection(id, !isSelected(id))}
            >
              <Checkbox checked={isSelected(id)} />
              {name}
            </Box>
          ))}
          {templates?.length > 0 && (
            <Button
              color="primary"
              sx={{ mt: 2, minWidth: '85px' }}
              onClick={handleSelectAll}
            >
              {isAllSelected ? 'Deselect all' : 'Select All'}
            </Button>
          )}
        </Box>
        {selected.length > 0 && (
          <Box marginRight="140px">
            <Typography variant="h1" sx={{ mb: 2.4 }}>
              Preview
            </Typography>
            {renderPreview()}
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent="center">
        <Button
          type="submit"
          color="primary"
          sx={{ mt: 2, mb: 2, minWidth: '85px' }}
          onClick={handleAddToDialer}
        >
          Add to Dialer
        </Button>
      </Box>
    </Box>
  );
};

export default LibraryTemplates;
